import { useState, useMemo, useEffect } from 'react';
import { Stack, Group } from '@mantine/core';
import { useSchemaForm } from '@ayocore/platform/client/hooks/useSchemaForm';
import { RepositorySelect } from '@ayocore/platform/client/components/RepositorySelect/RepositorySelect';
import { usePartnerRepository } from '@ayocore/erp/crm/client/rpc';
import {
  useAccountingDocumentRepository,
  useAccountingDocumentTransactionRepository,
} from '@ayocore/erp/accounting/client/rpc';
import accountingDocumentTransactionSchemaDefinition from '@ayocore/erp/accounting/schemas/document-transaction.schema';
import logger from '@ayocore/platform/common/helpers/logger';

interface CreateDealExpenseModalProps extends Record<string, unknown> {
  dealId: number;
  /** Заголовок передаётся через useModal и используется автоматически */
  title?: string;
  /** Вызывается после успешного создания расхода */
  onCreated?: () => void;
}

/**
 * Содержимое модалки создания расхода по сделке.
 *
 * Создаёт запись `accounting_document_transactions` с привязкой к сделке и документу.
 * Поле "Партнер" является UI-фильтром для выбора документа и не сохраняется в транзакцию.
 */
export function CreateDealExpenseModal({ dealId, onCreated }: CreateDealExpenseModalProps) {
  const partnerRepository = usePartnerRepository();
  const documentRepository = useAccountingDocumentRepository();
  const transactionRepository = useAccountingDocumentTransactionRepository();

  /** Партнёр — только UI-фильтр для документов, в схеме отсутствует */
  const [partnerId, setPartnerId] = useState<number | null>(null);

  const SchemaForm = useSchemaForm(accountingDocumentTransactionSchemaDefinition.createSchema, {
    hideDescriptions: true,
  });

  useEffect(() => {
    SchemaForm.setFieldValue('operation_date', new Date().toISOString().slice(0, 10));
  }, []);

  /** Репозиторий документов, фильтруется по выбранному партнёру */
  const filteredDocumentRepository = useMemo(
    () =>
      partnerId
        ? documentRepository.filterByPartner(partnerId).orderBy('operation_date', 'desc')
        : documentRepository.orderBy('operation_date', 'desc'),
    [documentRepository, partnerId]
  );

  const handlePartnerChange = (value: string | null) => {
    setPartnerId(value ? parseInt(value, 10) : null);
    SchemaForm.setFieldValue('document_id', null);
  };

  /** При выборе документа автоматически подставляем его сумму */
  const handleDocumentChange = async (value: string | null) => {
    const id = value ? parseInt(value, 10) : null;
    SchemaForm.setFieldValue('document_id', id);
    if (!id) return;
    try {
      const doc = await documentRepository.findById(id);
      if (doc?.total_amount != null) {
        SchemaForm.setFieldValue('amount', parseFloat(String(doc.total_amount)));
      }
    } catch (err) {
      logger.warn('Не удалось загрузить сумму документа:', err);
    }
  };

  const handleSubmit = async (values: typeof SchemaForm.values) => {
    try {
      await transactionRepository.create({
        document_id: values.document_id as number,
        amount: values.amount as number,
        operation_date: values.operation_date as string,
        description: (values.description as string | null) ?? null,
        deal_id: dealId,
      });
      SchemaForm.setSuccess('Расход добавлен');
      setTimeout(() => onCreated?.(), 800);
      return { success: true };
    } catch (err) {
      logger.error('Ошибка при создании расхода:', err);
      SchemaForm.setError(err instanceof Error ? err.message : 'Ошибка при создании расхода');
      return { success: false };
    }
  };

  return (
    <form onSubmit={SchemaForm.handleSubmit(handleSubmit)}>
      <Stack gap='sm'>
        <SchemaForm.Messages />

        {/* Партнер — только для фильтрации документов в выпадающем списке */}
        <RepositorySelect
          label='Партнер'
          size='xs'
          repo={partnerRepository.orderBy('name', 'asc')}
          displayField='name'
          placeholder='Любой партнёр...'
          enableServerSearch={true}
          searchDebounce={300}
          minSearchLength={2}
          onChange={handlePartnerChange}
          value={partnerId?.toString() ?? ''}
        />

        {/* Документ — кастомный RepositorySelect с фильтрацией по партнёру */}
        <RepositorySelect
          key={partnerId ?? 'no-partner'}
          label='Документ'
          size='xs'
          repo={filteredDocumentRepository}
          displayField='number'
          getOptionLabel={item => {
            const doc = item as Record<string, unknown>;
            const number = doc.number || '—';
            const date = doc.operation_date
              ? new Date(String(doc.operation_date)).toLocaleDateString('ru-RU')
              : '';
            const amount =
              doc.total_amount != null ? Number(doc.total_amount).toLocaleString('ru-RU') : '';
            const parts = [number, date ? `от ${date}` : '', amount ? `(${amount})` : ''];
            return parts.filter(Boolean).join(' ');
          }}
          placeholder='Выберите документ...'
          enableServerSearch={true}
          searchDebounce={300}
          onChange={value => void handleDocumentChange(value)}
          value={SchemaForm.values.document_id?.toString() ?? ''}
          error={
            SchemaForm.showValidationErrors && SchemaForm.errors.document_id
              ? String(SchemaForm.errors.document_id)
              : undefined
          }
        />

        <SchemaForm.Field.operation_date size='xs' />
        <SchemaForm.Field.amount size='xs' />
        <SchemaForm.Field.description size='xs' />

        <Group justify='flex-end'>
          <SchemaForm.buttons.submit label='Добавить' size='xs' />
        </Group>
      </Stack>
    </form>
  );
}
